/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { string, shape, bool, func, instanceOf } from 'prop-types';
import { NavLink } from 'react-router-dom';
import { connect } from 'react-redux';
import classNames from 'clsx';

import p from 'polyglot/polyglot';

import { createUrl } from 'utils/urlHelper';

import { logout as logoutAction } from 'actions/auth';
import { showLoginModal as showLoginModalAction } from 'actions/login';

import { SIGNUP_STATUS } from 'reducers/user';

import Debug from 'components/Debug/Debug';
import Button from 'components/Button/Button';
import Avatar from 'components/Avatar/Avatar';

import styles from './menu.scss';

/**
 * This is the Menu component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class Menu extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    auth: shape({
      isAuthenticated: bool.isRequired,
      token: string,
    }).isRequired,
    user: shape({
      status: string,
    }).isRequired,
    logout: func.isRequired,
    showLoginModal: func.isRequired,
    active: bool,
    onClose: func,
    parentElem: shape({ current: instanceOf(Element) }).isRequired,
  };

  static defaultProps = {
    active: false,
    onClose: () => {},
  };

  /**
   * Create Menu.
   */
  constructor(props) {
    super(props);

    this.wrapper = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClick);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClick);
  }

  componentDidUpdate(prevProps) {
    const { active: prevActive } = prevProps;
    const { active } = this.props;

    if (prevActive !== active && active) {
      this.wrapper.current.focus();
    }
  }

  onClickLogout = event => {
    event.preventDefault();
    const { logout } = this.props;

    logout();
    window.location.href = createUrl('home');
  };

  onClickLogin = event => {
    event.preventDefault();

    const { showLoginModal } = this.props;

    showLoginModal();
  };

  handleClick = event => {
    const { active, onClose, parentElem } = this.props;

    const DRAWER_NODE = this.wrapper?.current;
    const BUTTON_NODE = parentElem?.current;

    if (
      !DRAWER_NODE.contains(event.target) &&
      !BUTTON_NODE.contains(event.target) &&
      active
    ) {
      onClose(event);
    }
  };

  renderUserMenu = () => {
    const { user } = this.props;

    const hasInboxLink =
      (user.is_client && user.can_post_jobs) ||
      (user.is_talent && user.can_search_jobs);
    const hasJobsLink = user.is_client && user.can_post_jobs;

    return (
      <>
        {/* {user.is_client && (
          <li>
            <NavLink to={createUrl('talents')} exact>
              {p.t('header.menu.browse')}
            </NavLink>
          </li>
        )} */}

        {/* {user.is_talent && ( 
          <li>
            <NavLink
              to={createUrl('account')}
              exact
              activeClassName={styles.selected}
            >
              {p.t('header.menu.home')}
            </NavLink>
          </li>
        )} */}

        <li>
          <NavLink
            to={createUrl('talents')}
            exact
            activeClassName={styles.selected}
          >
            {p.t('header.menu.browse')}
          </NavLink>
        </li>

        {hasInboxLink && (
          <li>
            <NavLink
              to={createUrl('inbox')}
              exact
              // strict
              className={styles.inboxLink}
              activeClassName={styles.selected}
            >
              {p.t('header.menu.inbox')}
            </NavLink>
          </li>
        )}

        {hasJobsLink && (
          <li>
            <NavLink to={createUrl('jobs')} activeClassName={styles.selected}>
              {p.t('header.menu.client.jobs')}
            </NavLink>
          </li>
        )}

        <li>
          <NavLink
            to={createUrl('editProfile')}
            exact
            activeClassName={styles.selected}
          >
            {p.t('profile.title')}
          </NavLink>
        </li>

        {user.is_client && (
          <li>
            <NavLink
              to={createUrl('editCompany')}
              exact
              activeClassName={styles.selected}
            >
              {p.t('company.title')}
            </NavLink>
          </li>
        )}

        {user.is_talent && (
          <>
            <li>
              <NavLink
                to={createUrl('editPortfolio')}
                activeClassName={styles.selected}
              >
                {p.t('portfolio.title.portfolio')}
              </NavLink>
            </li>
            <li>
              <NavLink
                to={createUrl('payments')}
                activeClassName={styles.selected}
              >
                {p.t('payments.title')}
              </NavLink>
            </li>
          </>
        )}

        {user.show_admin && (
          <li>
            <NavLink
              to={createUrl('pageAdmin')}
              className={styles.admin}
              activeClassName={styles.selected}
            >
              Admin Exports
            </NavLink>
          </li>
        )}
      </>
    );
  };

  /**
   * Render Menu.
   *
   * @return {Object}
   */
  render() {
    const {
      active,
      user,
      auth: { isAuthenticated },
    } = this.props;

    const isApproved = user.status === SIGNUP_STATUS.APPLICATION_APPROVED;

    return (
      <nav
        tabIndex="0"
        ref={this.wrapper}
        className={classNames(styles.menu, {
          [styles.visible]: active,
        })}
      >
        <ul className={styles.links}>
          {isAuthenticated && isApproved && this.renderUserMenu()}

          {isAuthenticated && (
            <li className={styles.profile}>
              <div className={styles.user}>
                <Avatar src={user?.avatar?.round_thumbnail_url}>
                  {user.fullname}
                </Avatar>
                <p className={styles.username}>{user.fullname}</p>
              </div>
              <Button ghost dark small onClick={this.onClickLogout}>
                {p.t('header.menu.logout')}
              </Button>
            </li>
          )}

          {isAuthenticated && user.show_dev && (
            <li className={styles.debug}>
              <Debug />
            </li>
          )}

          {!isAuthenticated && [
            <li key="home-link">
              <NavLink to={createUrl('home')} activeClassName={styles.selected}>
                {p.t('header.menu.home')}
              </NavLink>
            </li>,
            <li key="news-link">
              <NavLink to={createUrl('news')} activeClassName={styles.selected}>
                {p.t('header.menu.news')}
              </NavLink>
            </li>,
            <li key="agency-link">
              <NavLink to="/agency/models" activeClassName={styles.selected}>
                Agency
              </NavLink>
            </li>,
            <li key="talent-link">
              <NavLink to="/talent/models" activeClassName={styles.selected}>
                Talent
              </NavLink>
            </li>,
            <li key="about-link">
              <NavLink to="/about-us" activeClassName={styles.selected}>
                {p.t('header.menu.about')}
              </NavLink>
            </li>,
            <li key="login-link">
              <Button
                dark
                ghost
                small
                onClick={this.onClickLogin}
                className={styles.button}
              >
                {p.t('header.login_button')}
              </Button>
            </li>,
          ]}
        </ul>
      </nav>
    );
  }
}

export default connect(({ auth, user }) => ({ auth, user }), {
  logout: logoutAction,
  showLoginModal: showLoginModalAction,
})(Menu);
